import {useCallback, useEffect, useRef, useState} from "react";
import {useOrg} from "@/hooks/useOrg";
import {SkillsService} from "@/services/skills/client";
import {AgentSkill} from "@/types/skills";

// Module-level cache of resolved skills, keyed by orgId. Re-mounting the welcome
// page (e.g. navigating back from a chat) re-uses the cached array so the user
// doesn't see the skills grid flash through a loading state on every visit.
// Refresh in the background on mount so returning to the page picks up shared
// catalog publications and flag changes without a loading flash.
const skillsCache = new Map<string, AgentSkill[]>();
const inFlightRequests = new Map<string, Promise<AgentSkill[]>>();

function loadSkills(orgId: string): Promise<AgentSkill[]> {
   const existing = inFlightRequests.get(orgId);
   if (existing) return existing;

   const request = SkillsService.listAll().finally(() => {
      if (inFlightRequests.get(orgId) === request) {
         inFlightRequests.delete(orgId);
      }
   });
   inFlightRequests.set(orgId, request);
   return request;
}

// Test-only escape hatch so unit tests can isolate cache state between cases.
export function __resetSkillsCache() {
   skillsCache.clear();
   inFlightRequests.clear();
}

export function useSkills() {
   const {org} = useOrg();
   const orgId = org.id ?? "default";

   const cached = skillsCache.get(orgId) ?? null;
   const [skills, setSkills] = useState<AgentSkill[] | null>(cached);
   const [isLoading, setIsLoading] = useState(cached === null);
   const lastFetchedOrgId = useRef<string | null>(null);
   // Monotonically increasing counter to discard stale fetches after org switches
   const fetchGeneration = useRef(0);

   const fetchSkills = useCallback(async () => {
      const generation = ++fetchGeneration.current;
      try {
         setIsLoading(!skillsCache.has(orgId));
         const data = await loadSkills(orgId);

         // Discard result if org changed while this request was in-flight
         if (generation !== fetchGeneration.current) return;

         skillsCache.set(orgId, data);
         setSkills(data);
      } catch (error) {
         if (generation !== fetchGeneration.current) return;
         console.error("Error fetching skills:", error);
         // Preserve existing skills on transient errors instead of clearing
         setSkills((prev) => prev ?? []);
      } finally {
         // Only clear loading for the latest fetch — stale fetches skip this,
         // but that's safe because the newer fetch will clear it when it resolves.
         if (generation === fetchGeneration.current) {
            setIsLoading(false);
         }
      }
   }, [orgId]);

   useEffect(() => {
      if (lastFetchedOrgId.current === orgId) return;
      lastFetchedOrgId.current = orgId;

      // Bump the generation on every org change so any in-flight fetch from the
      // previous org gets discarded before displaying cached data for this org.
      fetchGeneration.current++;

      const cachedForOrg = skillsCache.get(orgId);
      setSkills(cachedForOrg ?? null);
      fetchSkills();
   }, [fetchSkills, orgId]);

   return {skills, isLoading, refetch: fetchSkills};
}
